import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import {
  POST_CATEGORY,
  POST_SUBCATEGORY,
  GET_CATEGORIES,
  UPDATE_CATEGORY,
  UPDATE_SUBCATEGORY,
  DELETE_CATEGORY,
  DELETE_SUBCATEGORY,
  GET_SITE_CATEGORIES,
  GET_PAGE_TYPE_OPTIONS,
  DELETE_SITE_CAT,
  ADD_SITE_CAT,
  GET_CATEGORIES_TO_ADD,
  CLEAR_MESSAGE,
  PATCH_CATEGORY,
  CLEAR_SUBCATEGORIES,
  GET_SUBCATEGORY_BY_ID,
} from "./categoryTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { getCurrentSite } from "../siteServices/siteActions";
import { errorPayload } from "../caughtError";
// Response shapes come from the shared server/client contracts (SCRUM-983).
import type { AxiosResponse } from "axios";
import type {
  CategoriesResponse,
  CategoryCreateResponse,
  MessageResponse,
  PageTypeOptionsResponse,
  SubcategoryByIdResponse,
  SiteCategoriesResponse,
  CategoriesRemovedResponse,
} from "../../../../shared/apiTypes";

export const clearMessage = () => (dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
  });
};
/*********************** CATEGORY ************************/

// get categories
export const getCategories = createAppAsyncThunk(
  "categories/getAll",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<CategoriesResponse> = await api.get("/api/category/");
    // SCRUM-1019: guard the reverse (a non-array response used to be caught but
    // swallowed to a generic error) and let a failed fetch reject to the central
    // hub. The legacy GET_CATEGORIES action stays (categorySlice answers it, SCRUM-1953).
    dispatch({
      type: GET_CATEGORIES,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    return res.data;
  }
);

// Add Category
export const addCategory = (data) => async (dispatch, getState) => {
  try {
    const { site } = getState().sites;
    const payload = { ...data, country_code: site.country_code };
    const res: AxiosResponse<CategoryCreateResponse> = await api.post("/api/category/add", payload);
    
    dispatch({
      type: POST_CATEGORY,
      payload: res.data,
    });

    dispatch(getSiteCategoriesToAdd());
    dispatch(getSiteCategories());
    dispatch(getCategories());
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
  }
};

// update Category
export const updateCategory = (data) => (dispatch) => {
  api
    .put("/api/category/update", data)
    .then((res: AxiosResponse<MessageResponse>) => {
      dispatch({
        type: UPDATE_CATEGORY,
        payload: res.data,
      });
    })
    .then(() => {
      dispatch(getCategories());
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      });
    });
};

// delete Category — SCRUM-840: createAppAsyncThunk so a failed DELETE rejects
// (UpdateCategoriesContainer no longer shows "Category Deleted" on failure) and
// surfaces via the central error hub. Refresh kept; success GET_ERRORS {} reset
// dropped (the hub owns the error lifecycle now).
export const deleteCategory = createAppAsyncThunk(
  "categories/deleteCategory",
  async (id: string, { dispatch }) => {
    await api.delete<unknown>(`/api/category/delete/${id}`);
    dispatch({ type: DELETE_CATEGORY, payload: id });
    dispatch(getCategories());
    return id;
  }
);

/*********************** SUBCATEGORY ************************/

// Add Subcategory
export const addSubcategory = (data) => (dispatch) => {
  api
    .post(
      `/api/subcategory/add/${data.category._id}`,
      data
    )
    .then((res: AxiosResponse<MessageResponse>) => {
      dispatch({
        type: POST_SUBCATEGORY,
        payload: res.data,
      });
    })
    .catch((err) => {
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      });
    });
};

// update subategory
export const updateSubategory = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.put("/api/subcategory/update", data);
    
    dispatch({
      type: UPDATE_SUBCATEGORY,
      payload: res.data,
    });

    dispatch(getCategories());
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
  }
};

// delete Subcategory — SCRUM-840: createAppAsyncThunk so a failed DELETE rejects
// and surfaces via the central hub (the caller drives its own getCategories
// refresh, as before). Success GET_ERRORS {} reset dropped.
export const deleteSubcategory = createAppAsyncThunk(
  "categories/deleteSubcategory",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<CategoriesRemovedResponse> = await api.delete(`/api/subcategory/delete/${id}`);
    dispatch({ type: DELETE_SUBCATEGORY, payload: res.data });
    return res.data;
  }
);

export const clearSubcategories = () => (dispatch) => {
  dispatch({
    type: CLEAR_SUBCATEGORIES,
  });
};

// SCRUM-1653 slice 2: the page-type picker in one request. GET /api/pages/type-options
// answers the site's categories → subcategories → products (slice 1, projected);
// the reducer keeps the flat subcategory list in the site's category order, the
// shape the two page-type containers and PageFormContainer read. Replaces the
// per-category getSubCategoriesbyIds loop (1 + N requests) and its appended list.
export const getPageTypeOptions = createAppAsyncThunk(
  "categories/getPageTypeOptions",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<PageTypeOptionsResponse> = await api.get("/api/pages/type-options");
    const categories = Array.isArray(res.data?.categories) ? res.data.categories : [];
    dispatch({
      type: GET_PAGE_TYPE_OPTIONS,
      payload: categories.flatMap((cat) =>
        Array.isArray(cat?.subcategories) ? cat.subcategories : []
      ),
    });
    dispatch({ type: GET_ERRORS, payload: {} });
    return res.data;
  }
);

//get sub category by id
export const getSubCategorybyId = (idSubcategory) => async (dispatch) => {
  try {
    const res: AxiosResponse<SubcategoryByIdResponse> = await api.get(`/api/subcategory/${idSubcategory}`);
    
    dispatch({
      type: GET_SUBCATEGORY_BY_ID,
      payload: res.data,
    });

    dispatch({ type: GET_ERRORS, payload: {} });
  } catch (error) {
    // Was `payload: error`, the raw axios Error: getErrorMessage reads its
    // `.message` ("Request failed with status code N") and the server's own reason
    // never reached the user. Same guard as the SCRUM-1019 reads above.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(error),
    });
  }
};

/******************************** AFFECTATION  ********************************/
//add site category
export const addSiteCategory = (idCategory) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.put(
      `/api/category/addcategorytosite/${idCategory}`
    );
    dispatch({
      type: ADD_SITE_CAT,
      payload: res.data,
    });
    dispatch(getSiteCategoriesToAdd());
    dispatch(getSiteCategories());
    dispatch(getCurrentSite());
    dispatch({ type: GET_ERRORS, payload: {} });
    // Surface success + re-throw on failure (SCRUM-815). The old chain had no .catch at
    // all, so a failed assignment was an unhandled rejection while the caller showed
    // "Category assigned to site".
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};
// get site categories to add
export const getSiteCategoriesToAdd = createAppAsyncThunk(
  "categories/getSiteToAdd",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<CategoriesResponse> = await api.get("/api/category/toaddcategories");
    // SCRUM-1019: this read had NO .catch — a failure was an unhandled rejection
    // and a non-array response crashed on .reverse(). Now guarded + it rejects to
    // the central hub. The success reset is kept.
    dispatch({
      type: GET_CATEGORIES_TO_ADD,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    dispatch({ type: GET_ERRORS, payload: {} });
    return res.data;
  }
);

// get site categories
export const getSiteCategories = () => async (dispatch) => {
  let res: AxiosResponse<SiteCategoriesResponse>;
  // Keep the success dispatches out of the try. `res.data[0].categories.reverse()`
  // used to run inside it, so an empty `[]` response — a site with no categories —
  // threw and was then reported to the user as a failed request (SCRUM-1278).
  try {
    res = await api.get("/api/category/sites/");
  } catch (error) {
    // Was `payload: error`, the raw axios Error: getErrorMessage reads its
    // `.message` ("Request failed with status code 500") and the server's own
    // reason was never surfaced. Same guard as the SCRUM-1019 siblings above.
    return dispatch({
      type: GET_ERRORS,
      payload: errorPayload(error),
    });
  }
  const first = Array.isArray(res.data) ? res.data[0] : undefined;
  dispatch({
    type: GET_SITE_CATEGORIES,
    payload: Array.isArray(first?.categories)
      ? [...first.categories].reverse()
      : [],
  });
  return dispatch({ type: GET_ERRORS, payload: {} });
};

//delete Site Category
export const deleteSiteCategory = (idCategory) => async (dispatch) => {
  try {
    const res: AxiosResponse<CategoriesRemovedResponse> = await api.put(
      `/api/category/removesitecategory/${idCategory}`
    );
    dispatch({
      type: DELETE_SITE_CAT,
      payload: res.data,
    });
    dispatch(getSiteCategoriesToAdd());
    dispatch(getSiteCategories());
    dispatch(getCurrentSite());
    dispatch({ type: GET_ERRORS, payload: {} });
    // Surface success + re-throw on failure (SCRUM-815). The old chain had no .catch, so a
    // failed removal was an unhandled rejection while the caller showed "Category removed".
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};
// *****************************************************************// 
//patch category => Add label to the current website
//
// SCRUM-1704: resolves to `{ ok: true }` or `{ ok: false, error }` so the label
// screen can show a refusal on the field instead of navigating away from it —
// SCRUM-1703 answers 400 OVERLAY_SLUG_INVALID / 409 DUPLICATE_OVERLAY_SLUG
// before writing anything, and the old chain returned nothing, so the caller
// could not tell a refusal from a save. The GET_ERRORS dispatch stays (the
// error-handler suite pins it); the promise never rejects.
export type PatchCategoryResult = { ok: true } | { ok: false; error: unknown };
export const patchCategory = (data) => (dispatch): Promise<PatchCategoryResult> =>
  api
    .patch("/api/category/patch", data)
    .then((res: AxiosResponse<MessageResponse>) => {
      dispatch({
        type: PATCH_CATEGORY,
        payload: res.data,
      });
    })
    .then(() => {
      dispatch(getSiteCategoriesToAdd());
      dispatch(getSiteCategories());
      dispatch(getCurrentSite());
      return { ok: true } as const;
    })
    .catch((err) => {
      const error = err?.response?.data ?? { message: err?.message };
      dispatch({
        type: GET_ERRORS,
        payload: error,
      });
      return { ok: false, error } as const;
    });
